import apiClient from './api';

export interface DocumentClassificationLabelDto {
  documentClassificationLabelId: number;
  documentLabelSetId: number;
  classificationLabel: string;
  classificationDescription?: string;
  classificationPrompt: string;
  seedDocumentName?: string;
  isAutoGenerated: boolean;
  isActive: boolean;
}

export interface DocumentLabelSetDto {
  documentLabelSetId: number;
  labelSetName: string;
  labelSetDescription?: string;
  isActive: boolean;
  createdDate: string;
  labels: DocumentClassificationLabelDto[];
}

export interface CreateDocumentLabelSetRequest {
  labelSetName: string;
  labelSetDescription?: string;
}

export interface UpdateDocumentClassificationLabelRequest {
  classificationLabel: string;
  classificationDescription?: string;
  classificationPrompt: string;
  seedDocumentName?: string;
  seedDocumentHash?: string;
  isAutoGenerated?: boolean;
  isActive?: boolean;
}

export interface UpdateDocumentLabelSetRequest {
  labelSetName?: string;
  labelSetDescription?: string;
  isActive?: boolean;
  labels?: UpdateDocumentClassificationLabelRequest[];
}

export interface CreateDocumentClassificationLabelRequest {
  classificationLabel: string;
  classificationDescription?: string;
  classificationPrompt: string;
  seedDocumentName?: string;
  seedDocumentHash?: string;
  isAutoGenerated?: boolean;
}

export interface DocumentSummarisationTemplateDto {
  documentSummarisationTemplateId: number;
  summarisationName: string;
  summarisationDescription?: string;
  summarisationPrompt: string;
  isActive: boolean;
  createdDate: string;
}

export interface CreateDocumentSummarisationTemplateRequest {
  summarisationName: string;
  summarisationDescription?: string;
  summarisationPrompt: string;
}

export interface UpdateDocumentSummarisationTemplateRequest {
  summarisationName?: string;
  summarisationDescription?: string;
  summarisationPrompt?: string;
  isActive?: boolean;
}

export interface DocumentExtractionFieldDto {
  documentExtractionFieldId: number;
  documentExtractionTemplateId: number;
  fieldName: string;
  exampleValue?: string;
  anchorTextBefore?: string;
  anchorTextAfter?: string;
  fieldExtractionPrompt?: string;
  pageNumber?: number;
  boundingBoxJson?: string;
  isActive: boolean;
}

export interface DocumentExtractionTemplateDto {
  documentExtractionTemplateId: number;
  extractionTemplateName: string;
  extractionTemplateDescription?: string;
  isActive: boolean;
  createdDate: string;
  fields: DocumentExtractionFieldDto[];
}

export interface CreateDocumentExtractionTemplateRequest {
  extractionTemplateName: string;
  extractionTemplateDescription?: string;
}

export interface UpdateDocumentExtractionFieldRequest {
  fieldName: string;
  exampleValue?: string;
  anchorTextBefore?: string;
  anchorTextAfter?: string;
  fieldExtractionPrompt?: string;
  pageNumber?: number;
  boundingBoxJson?: string;
  isActive?: boolean;
}

export interface UpdateDocumentExtractionTemplateRequest {
  extractionTemplateName?: string;
  extractionTemplateDescription?: string;
  isActive?: boolean;
  fields?: UpdateDocumentExtractionFieldRequest[];
}

export interface CreateDocumentExtractionFieldRequest {
  fieldName: string;
  exampleValue?: string;
  anchorTextBefore?: string;
  anchorTextAfter?: string;
  fieldExtractionPrompt?: string;
  pageNumber?: number;
  boundingBoxJson?: string;
}

export interface DocumentClassificationSuggestionDto {
  fileName: string;
  suggestedLabel: string;
  suggestedDescription: string;
  suggestedPrompt: string;
  textPreview: string;
}

export interface DocumentClassificationTestResponse {
  fileName: string;
  classificationLabel: string;
  classificationDescription?: string;
  classificationScore: number;
  classificationExplainability: string;
  documentClassificationLabelId?: number;
  textPreview: string;
}

export interface DocumentSummarisationPreviewResponse {
  fileName: string;
  promptUsed: string;
  summary: string;
  textPreview: string;
}

export interface DocumentExtractionPreviewResponse {
  fileName: string;
  extractedText: string;
  textPreview: string;
  suggestedFields: DocumentExtractionSuggestedFieldDto[];
  extractionTemplateId?: number;
  /** template_fields matches saved template + workflows; ai_suggestions is generic guessing */
  previewMode?: string;
}

export interface DocumentExtractionSuggestedFieldDto {
  fieldName: string;
  exampleValue: string;
  normalizedToken?: string;
}

export interface SuggestExtractionFromSelectionRequest {
  selectedText: string;
  extractedText?: string;
}

export interface TriggerPropertyHubEmailProcessingRequest {
  mailboxUser?: string;
  maxEmails?: number;
}

export interface TriggerPropertyHubEmailProcessingResponse {
  status: string;
  message: string;
  processingResult?: unknown;
}

export interface DocumentWorkflowStepDto {
  documentWorkflowStepId: number;
  documentWorkflowRuleId: number;
  stepOrder: number;
  stepType: string;
  stepConfigJson?: string;
  isActive: boolean;
}

export interface DocumentWorkflowRuleDto {
  documentWorkflowRuleId: number;
  workflowName: string;
  classificationLabel: string;
  minimumScore: number;
  priority: number;
  stopOnFailure: boolean;
  isActive: boolean;
  createdDate: string;
  steps: DocumentWorkflowStepDto[];
}

export interface UpsertDocumentWorkflowStepRequest {
  stepOrder: number;
  stepType: string;
  stepConfigJson?: string;
  isActive?: boolean;
}

export interface CreateDocumentWorkflowRuleRequest {
  workflowName: string;
  classificationLabel: string;
  minimumScore: number;
  priority: number;
  stopOnFailure: boolean;
  steps: UpsertDocumentWorkflowStepRequest[];
}

export interface UpdateDocumentWorkflowRuleRequest {
  workflowName?: string;
  classificationLabel?: string;
  minimumScore?: number;
  priority?: number;
  stopOnFailure?: boolean;
  isActive?: boolean;
  steps?: UpsertDocumentWorkflowStepRequest[];
}

export interface DocumentWorkflowStepTestResultDto {
  stepOrder: number;
  stepType: string;
  status: string;
  details: string;
}

export interface DocumentWorkflowRuleTestResponse {
  documentWorkflowRuleId: number;
  workflowName: string;
  classificationLabel: string;
  classificationScore: number;
  ruleEligible: boolean;
  eligibilityReason: string;
  steps: DocumentWorkflowStepTestResultDto[];
}

export interface DocumentWorkflowExtractionSnapshotDto {
  fieldName: string;
  fieldValue?: string;
  comments?: string;
}

export interface DocumentWorkflowAuditRunDto {
  documentWorkflowAuditRunId: number;
  subject?: string;
  classificationLabel?: string;
  classificationScore?: number;
  status: string;
  errorMessage?: string;
  startedDate: string;
  completedDate?: string;
  summarisationText?: string;
  extractionSnapshots: DocumentWorkflowExtractionSnapshotDto[];
}

export interface DocumentWorkflowRuleRunHistoryResponse {
  documentWorkflowRuleId: number;
  workflowName: string;
  runs: DocumentWorkflowAuditRunDto[];
}

export const documentHubService = {
  getLabelSets: async (): Promise<DocumentLabelSetDto[]> => {
    return await apiClient<DocumentLabelSetDto[]>('/document-hub/label-sets');
  },

  createLabelSet: async (request: CreateDocumentLabelSetRequest): Promise<DocumentLabelSetDto> => {
    return await apiClient<DocumentLabelSetDto>('/document-hub/label-sets', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  updateLabelSet: async (labelSetId: number, request: UpdateDocumentLabelSetRequest): Promise<DocumentLabelSetDto> => {
    return await apiClient<DocumentLabelSetDto>(`/document-hub/label-sets/${labelSetId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  deleteLabelSet: async (labelSetId: number): Promise<void> => {
    await apiClient<void>(`/document-hub/label-sets/${labelSetId}`, {
      method: 'DELETE',
    });
  },

  createClassificationLabel: async (
    labelSetId: number,
    request: CreateDocumentClassificationLabelRequest
  ): Promise<DocumentClassificationLabelDto> => {
    return await apiClient<DocumentClassificationLabelDto>(`/document-hub/label-sets/${labelSetId}/labels`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  getSummarisationTemplates: async (): Promise<DocumentSummarisationTemplateDto[]> => {
    return await apiClient<DocumentSummarisationTemplateDto[]>('/document-hub/summarisation-templates');
  },

  createSummarisationTemplate: async (
    request: CreateDocumentSummarisationTemplateRequest
  ): Promise<DocumentSummarisationTemplateDto> => {
    return await apiClient<DocumentSummarisationTemplateDto>('/document-hub/summarisation-templates', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  updateSummarisationTemplate: async (
    templateId: number,
    request: UpdateDocumentSummarisationTemplateRequest
  ): Promise<DocumentSummarisationTemplateDto> => {
    return await apiClient<DocumentSummarisationTemplateDto>(`/document-hub/summarisation-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  deleteSummarisationTemplate: async (templateId: number): Promise<void> => {
    await apiClient<void>(`/document-hub/summarisation-templates/${templateId}`, {
      method: 'DELETE',
    });
  },

  getExtractionTemplates: async (): Promise<DocumentExtractionTemplateDto[]> => {
    return await apiClient<DocumentExtractionTemplateDto[]>('/document-hub/extraction-templates');
  },

  createExtractionTemplate: async (
    request: CreateDocumentExtractionTemplateRequest
  ): Promise<DocumentExtractionTemplateDto> => {
    return await apiClient<DocumentExtractionTemplateDto>('/document-hub/extraction-templates', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  updateExtractionTemplate: async (
    templateId: number,
    request: UpdateDocumentExtractionTemplateRequest
  ): Promise<DocumentExtractionTemplateDto> => {
    return await apiClient<DocumentExtractionTemplateDto>(`/document-hub/extraction-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  deleteExtractionTemplate: async (templateId: number): Promise<void> => {
    await apiClient<void>(`/document-hub/extraction-templates/${templateId}`, {
      method: 'DELETE',
    });
  },

  createExtractionField: async (
    templateId: number,
    request: CreateDocumentExtractionFieldRequest
  ): Promise<DocumentExtractionFieldDto> => {
    return await apiClient<DocumentExtractionFieldDto>(`/document-hub/extraction-templates/${templateId}/fields`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  suggestClassificationLabels: async (files: File[]): Promise<DocumentClassificationSuggestionDto[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return await apiClient<DocumentClassificationSuggestionDto[]>('/document-hub/classification/suggest', {
      method: 'POST',
      body: formData,
    });
  },

  testClassification: async (file: File): Promise<DocumentClassificationTestResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    return await apiClient<DocumentClassificationTestResponse>('/document-hub/classification/test', {
      method: 'POST',
      body: formData,
    });
  },

  previewSummarisation: async (file: File, prompt: string): Promise<DocumentSummarisationPreviewResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('prompt', prompt);

    return await apiClient<DocumentSummarisationPreviewResponse>('/document-hub/summarisation/preview', {
      method: 'POST',
      body: formData,
    });
  },

  previewExtraction: async (
    file: File,
    extractionTemplateId?: number | null
  ): Promise<DocumentExtractionPreviewResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (extractionTemplateId != null && extractionTemplateId > 0) {
      formData.append('extractionTemplateId', String(extractionTemplateId));
    }

    return await apiClient<DocumentExtractionPreviewResponse>('/document-hub/extraction/preview', {
      method: 'POST',
      body: formData,
    });
  },

  suggestExtractionFromSelection: async (
    request: SuggestExtractionFromSelectionRequest
  ): Promise<DocumentExtractionSuggestedFieldDto[]> => {
    return await apiClient<DocumentExtractionSuggestedFieldDto[]>('/document-hub/extraction/selection-suggest', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  triggerPropertyHubEmailProcessing: async (
    request: TriggerPropertyHubEmailProcessingRequest
  ): Promise<TriggerPropertyHubEmailProcessingResponse> => {
    return await apiClient<TriggerPropertyHubEmailProcessingResponse>('/document-hub/email-processing/property-hub/trigger', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  getWorkflowRules: async (): Promise<DocumentWorkflowRuleDto[]> => {
    return await apiClient<DocumentWorkflowRuleDto[]>('/document-hub/workflow-rules');
  },

  createWorkflowRule: async (request: CreateDocumentWorkflowRuleRequest): Promise<DocumentWorkflowRuleDto> => {
    return await apiClient<DocumentWorkflowRuleDto>('/document-hub/workflow-rules', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  updateWorkflowRule: async (
    ruleId: number,
    request: UpdateDocumentWorkflowRuleRequest
  ): Promise<DocumentWorkflowRuleDto> => {
    return await apiClient<DocumentWorkflowRuleDto>(`/document-hub/workflow-rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  deleteWorkflowRule: async (ruleId: number): Promise<void> => {
    await apiClient<void>(`/document-hub/workflow-rules/${ruleId}`, {
      method: 'DELETE',
    });
  },

  testWorkflowRule: async (ruleId: number, file: File): Promise<DocumentWorkflowRuleTestResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    return await apiClient<DocumentWorkflowRuleTestResponse>(`/document-hub/workflow-rules/${ruleId}/test`, {
      method: 'POST',
      body: formData,
    });
  },

  getWorkflowRuleRunHistory: async (
    ruleId: number,
    limit = 50
  ): Promise<DocumentWorkflowRuleRunHistoryResponse> => {
    return await apiClient<DocumentWorkflowRuleRunHistoryResponse>(
      `/document-hub/workflow-rules/${ruleId}/run-history?limit=${limit}`
    );
  },
};
